/**
 * Display formatting for phone numbers.
 *
 * Tenants enter numbers as free text and the server keeps that text verbatim, deriving the E.164
 * form Twilio needs into a separate column. So the same number can be stored as "5551234567",
 * "(555) 123-4567" or "+15551234567" depending on who typed it and when. These helpers give all of
 * those one appearance wherever a number is shown.
 *
 * The rules deliberately mirror PhoneNumberUtilities.ToE164 on the server: ten digits, or eleven
 * beginning with one, are treated as US, and anything with an explicit "+" is taken at face value.
 * Keeping the two in step matters because a number this formats as US is a number the server will
 * text as US.
 */

/** Just the digits, for comparing or searching numbers written in different styles. */
export function phoneDigits(phone) {
    return phone ? String(phone).replace(/\D/g, '') : '';
}

function formatUsNational(tenDigits) {
    return `(${tenDigits.slice(0, 3)}) ${tenDigits.slice(3, 6)}-${tenDigits.slice(6)}`;
}

/**
 * Formats a phone number for display.
 *
 * Anything that cannot be recognised is returned exactly as it was stored rather than mangled or
 * hidden: a number that looks wrong on screen is a number someone can correct, whereas a blanked
 * field just looks like missing data.
 * @param phone {string|null|undefined}
 * @returns {string}
 */
export function formatPhoneNumber(phone) {
    if (!phone) {
        return '';
    }

    const trimmed = String(phone).trim();
    const digits = phoneDigits(trimmed);

    if (!digits) {
        return trimmed;
    }

    if (trimmed.startsWith('+')) {
        // A US number written in full still reads better nationally.
        if (digits.length === 11 && digits.startsWith('1')) {
            return formatUsNational(digits.slice(1));
        }

        // Grouping varies by country and guessing it wrong is worse than not grouping at all, so an
        // international number keeps its E.164 shape.
        return `+${digits}`;
    }

    if (digits.length === 10) {
        return formatUsNational(digits);
    }

    if (digits.length === 11 && digits.startsWith('1')) {
        return formatUsNational(digits.slice(1));
    }

    return trimmed;
}

/**
 * Whether this number can be turned into something Twilio will accept.
 *
 * Worth knowing at the point of entry, because a number that cannot be normalised means the tenant
 * silently never receives a text: they can still opt in, and nothing else tells them why nothing
 * arrives.
 * @param phone {string|null|undefined}
 * @returns {boolean}
 */
export function isTextablePhoneNumber(phone) {
    if (!phone) {
        return false;
    }

    const trimmed = String(phone).trim();
    const digits = phoneDigits(trimmed);

    if (trimmed.startsWith('+')) {
        // E.164 allows between 8 and 15 digits in total.
        return digits.length >= 8 && digits.length <= 15;
    }

    return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
}
